import React from "react";

import {CheckIcon, MinusIcon} from "@heroicons/react/20/solid";
import {Decimal} from "decimal.js";
import {P, match} from "ts-pattern";

import * as workouts from "@client/features/workouts";

import {Icon} from "@client/components/Icon";
import {cardioSummary} from "@client/components/cardio/model";
import * as widgets from "@client/forms/widgets";
import * as hardware from "@client/hardware";
import * as forms from "@client/site/forms";
import {classNames, models, server} from "@reactivated";

export function getSetPosition(
    sets: server.journal.workouts.ExerciseSet.output[],
    exerciseSetIndex: number,
) {
    return sets.reduce((accumulator, form, currentIndex) => {
        if (currentIndex >= exerciseSetIndex || form.type != "WORKING") {
            return accumulator;
        }

        return accumulator + 1;
    }, 1);
}

export function ExerciseSetCheck({
    handler,
    readOnly,
    isImperial,
    restTimerBetweenExercises,
}: {
    readOnly: boolean;
    handler: workouts.ExerciseSet;
    isImperial: boolean;
    restTimerBetweenExercises: boolean;
}) {
    const workoutContext = React.useContext(workouts.WorkoutContext);
    const exerciseContext = React.useContext(workouts.ExerciseContext);

    if (handler.status == "COMPLETE") {
        return (
            <button
                type="button"
                role="checkbox"
                aria-checked={true}
                disabled={readOnly}
                onClick={() => {
                    handler.__update({status: "INCOMPLETE"});
                    hardware.haptic("Light");
                }}
                className="
                    flex size-8 items-center justify-center rounded-full bg-green-100
                "
            >
                <CheckIcon className="size-5 text-green-600" aria-hidden="true" />
            </button>
        );
    } else if (handler.status == "SKIPPED") {
        return (
            <button
                type="button"
                disabled={readOnly}
                onClick={() => {
                    handler.__update({status: "INCOMPLETE"});
                    hardware.haptic("Light");
                }}
                className="
                    flex size-8 items-center justify-center rounded-full bg-sky-100
                "
            >
                <MinusIcon className="size-5 text-sky-600" aria-hidden="true" />
            </button>
        );
    }

    return (
        <button
            type="button"
            role="checkbox"
            aria-checked={false}
            disabled={readOnly}
            onClick={() => {
                const suggestedWeight =
                    handler.suggested_weight != null
                        ? new Decimal(handler.suggested_weight)
                              .times(isImperial ? "0.45359237" : "1")
                              .toString()
                        : null;

                handler.__update({
                    weight: handler.weight ?? suggestedWeight,
                    status: "COMPLETE",
                });
                hardware.haptic("Medium");

                // Check if this is the last set of the current exercise
                const allExercises =
                    workoutContext?.workout.sections
                        .flatMap((s) => s.groups)
                        .flatMap((g) => g.exercises) ?? [];
                const currentExerciseIndex = allExercises.findIndex(
                    (ex) => ex.uuid === exerciseContext?.exercise.uuid,
                );
                const isLastExercise = currentExerciseIndex === allExercises.length - 1;
                const currentSetIndex = exerciseContext?.exercise.sets.findIndex(
                    (s) => s.uuid === handler.uuid,
                );
                const isLastSetOfExercise =
                    currentSetIndex ===
                    (exerciseContext?.exercise.sets.length ?? 0) - 1;
                const isLastSetOfWorkout = isLastExercise && isLastSetOfExercise;

                if (isLastSetOfWorkout) {
                    // Clear timer if this is the last set of the entire workout
                    workoutContext?.startRestTimer(null);
                } else if (exerciseContext?.exercise.rest_duration != null) {
                    // Start rest timer based on the restTimerBetweenExercises flag
                    if (restTimerBetweenExercises) {
                        // Timer between sets AND exercises (just not the last set of workout)
                        workoutContext?.startRestTimer(
                            exerciseContext.exercise.rest_duration,
                        );
                    } else if (!isLastSetOfExercise) {
                        // Timer only between sets (not on last set of each exercise)
                        workoutContext?.startRestTimer(
                            exerciseContext.exercise.rest_duration,
                        );
                    }
                }
            }}
            className="
                flex size-8 items-center justify-center rounded-full bg-stone-200/50
            "
        >
            <CheckIcon className="size-5 text-stone-300" aria-hidden="true" />
        </button>
    );
}

export function ExerciseSetFields({
    handler,
    readOnly: _workoutReadOnly,
    isTemplate,
    category,
}: {
    handler: workouts.ExerciseSet;
    isTemplate: boolean;
    readOnly: boolean;
    // Strength-only: cardio renders through CardioSteady / CardioIntervals, never
    // here, so a cardio category can't even be passed.
    category: workouts.StrengthCategory;
}) {
    // Our set itself can be read only except the check mark without the workout
    // being read only.
    const readOnly = _workoutReadOnly || handler.status != "INCOMPLETE";
    const justForSelect = forms.usePform(
        {
            duration_type: {
                type: "select",
                widget: "select",
                options: [
                    ["SECONDS", "sec"],
                    ["MINUTES", "min"],
                    ["HOURS", "hours"],
                ],
                required: false,
            },
            distance_type: {
                type: "select",
                widget: "select",
                options: [
                    ["INCHES", "in"],
                    ["YARDS", "yd"],
                    ["FEET", "ft"],
                    ["METERS", "m"],
                    ["MILES", "mi"],
                    ["KILOMETERS", "km"],
                ],
                required: false,
            },
            duration: {
                type: "duration",
            },
        },
        handler,
        handler.__update,
        {readOnly},
    );

    const isImperial = forms.useIsImperial();

    const hasRPE = handler.weight_type == "RPE";
    const hasPercentage = handler.weight_type == "PERCENTAGE";

    const medleyForm = forms.usePform(
        {
            instructions: {
                type: "textarea",
                placeholder: "Instructions for these sets...",
            },
        },
        {instructions: handler.instructions},
        (values) => {
            handler.__update({instructions: values.instructions});
        },
    );

    const fieldClass = forms.getFieldClass({readOnly});
    const suffixClass = forms.getSuffixClass({readOnly});

    const check = isTemplate == false && (
        <ExerciseSetCheck
            readOnly={_workoutReadOnly}
            handler={handler}
            isImperial={isImperial}
            restTimerBetweenExercises={true}
        />
    );

    const getName = <K extends keyof server.journal.workouts.ExerciseSet.output>(
        key: K,
    ) => {
        return `${handler.uuid}-${String(key)}`;
    };

    const durationField = <forms.PWidget field={justForSelect.fields.duration} />;

    return match(category)
        .with("MEDLEY", () => (
            <div className="w-full space-y-1">
                <forms.PWidget
                    field={{...medleyForm.fields.instructions, readOnly: false}}
                    className={classNames(
                        fieldClass,
                        "min-h-[150px] sm:min-h[200px] block w-full rounded-md!",
                    )}
                />
                <div className="flex items-center gap-1">
                    {durationField}
                    <div>{check}</div>
                </div>
            </div>
        ))
        .with("REPS_DISTANCE", () => (
            <>
                <div className="flex flex-1 items-center gap-1">
                    <div className="flex w-full flex-1 rounded-md shadow-xs">
                        <widgets.DecimalInput
                            name={getName("distance")}
                            value={handler.distance?.toString() ?? ""}
                            readOnly={readOnly}
                            onChange={(value) => {
                                handler.__update({distance: value});
                            }}
                            className={classNames(fieldClass, "sm:max-w-none")}
                        />
                        <forms.PWidget
                            field={justForSelect.fields.distance_type}
                            className={classNames(
                                fieldClass,
                                "form-select",
                                "-ml-1 rounded-l-none rounded-r-md text-xs disabled:opacity-100",
                            )}
                        />
                    </div>
                    {/* This will never be true, RPE is just for weight exercises */}
                    {!hasRPE && <Icon icon="custom-bolder-cross" />}

                    <div className="flex-1">
                        <label htmlFor={getName("reps")} className="sr-only">
                            Reps
                        </label>
                        <div className="flex rounded-md shadow-xs">
                            <widgets.DecimalInput
                                name={getName("reps")}
                                value={handler.reps?.toString() ?? ""}
                                integerOnly
                                readOnly={readOnly}
                                onChange={(value) => {
                                    if (value == null) {
                                        handler.__update({reps: value});
                                    } else {
                                        handler.__update({reps: parseInt(value)});
                                    }
                                }}
                                className={fieldClass}
                            />
                            <span className={suffixClass}>Reps</span>
                        </div>
                    </div>
                </div>
                <div>{check}</div>
            </>
        ))
        .with(P.union("WEIGHT_DISTANCE", "WEIGHT_DURATION_DISTANCE"), () => (
            <>
                <div
                    className="
                        grid flex-1 grid-cols-[3fr_min-content_2fr] items-center gap-1
                    "
                >
                    <div className="flex rounded-md shadow-xs">
                        <widgets.DecimalInput
                            name={getName("distance")}
                            value={handler.distance?.toString() ?? ""}
                            readOnly={readOnly}
                            onChange={(value) => {
                                handler.__update({distance: value});
                            }}
                            className={classNames(fieldClass, "sm:max-w-none")}
                        />

                        <forms.PWidget
                            field={justForSelect.fields.distance_type}
                            className={classNames(
                                fieldClass,
                                "form-select",
                                "w-[50px]! sm:w-32! flex-none!",
                                "-ml-1 rounded-l-none rounded-r-md text-xs disabled:opacity-100",
                            )}
                        />
                    </div>
                    <div className="text-xs">with</div>
                    <div>
                        <label htmlFor={getName("weight")} className="sr-only">
                            Weight
                        </label>
                        <div className="flex rounded-md shadow-xs">
                            <widgets.WeightInput
                                isImperial={isImperial}
                                name={getName("weight")}
                                value={handler.weight}
                                onChange={(value) => handler.__update({weight: value})}
                                className={fieldClass}
                                readOnly={readOnly}
                            />
                            <span className={suffixClass}>
                                {isImperial ? "lb" : "kg"}
                            </span>
                        </div>
                    </div>
                    {category == "WEIGHT_DURATION_DISTANCE" && durationField}
                </div>
                {check}
            </>
        ))
        .with("DURATION_DISTANCE", () => (
            <>
                <div className="flex flex-1 items-center gap-1">
                    <div className="flex w-full flex-1 rounded-md shadow-xs">
                        <widgets.DecimalInput
                            name={getName("distance")}
                            value={handler.distance?.toString() ?? ""}
                            readOnly={readOnly}
                            onChange={(value) => {
                                handler.__update({distance: value});
                            }}
                            className={classNames(fieldClass, "sm:max-w-none")}
                        />
                        <forms.PWidget
                            field={justForSelect.fields.distance_type}
                            className={classNames(
                                fieldClass,
                                "form-select",
                                "-ml-1 rounded-l-none rounded-r-md text-xs disabled:opacity-100",
                            )}
                        />
                    </div>
                    <div className="flex-1">{durationField}</div>
                </div>
                <div>{check}</div>
            </>
        ))
        .with("DURATION", () => (
            <>
                <div
                    className={classNames(
                        "flex flex-col gap-1",
                        hasRPE == false && "sm:flex-row",
                    )}
                >
                    <div
                        className="
                            flex-1
                            sm:basis-3/5
                        "
                    >
                        {durationField}
                    </div>
                    <div className="flex gap-1">
                        <label htmlFor={getName("weight")} className="sr-only">
                            Weight
                        </label>
                        <div className="flex flex-1 gap-1">
                            <div className="flex flex-1 rounded-md shadow-xs">
                                <widgets.WeightInput
                                    isImperial={isImperial}
                                    name={getName("weight")}
                                    value={handler.weight}
                                    onChange={(value) =>
                                        handler.__update({weight: value})
                                    }
                                    className={fieldClass}
                                    readOnly={readOnly}
                                />
                                <span className={suffixClass}>
                                    {isImperial ? "lb" : "kg"}
                                </span>
                            </div>
                        </div>

                        {hasRPE && (
                            <>
                                <label htmlFor={getName("rpe")} className="sr-only">
                                    RPE
                                </label>
                                <div className="flex flex-1 rounded-md shadow-xs">
                                    <widgets.DecimalInput
                                        name={getName("rpe")}
                                        readOnly={readOnly}
                                        onChange={(value) =>
                                            handler.__update({rpe: value})
                                        }
                                        value={handler.rpe}
                                        className={fieldClass}
                                    />
                                    <span className={suffixClass}>RPE</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div>{check}</div>
            </>
        ))
        .with(P.union("WEIGHT", "BODYWEIGHT"), () => (
            <>
                {(isTemplate == false || hasPercentage == false) && (
                    <div className="flex-1">
                        <label htmlFor={getName("weight")} className="sr-only">
                            Weight
                        </label>
                        <div className="flex rounded-md shadow-xs">
                            {/* We use the status to remount in case we accept the suggested weight. */}
                            <widgets.WeightInput
                                key={handler.status}
                                isImperial={isImperial}
                                name={getName("weight")}
                                value={handler.weight}
                                onChange={(value) => handler.__update({weight: value})}
                                placeholder={
                                    (handler.status === "INCOMPLETE"
                                        ? handler.suggested_weight
                                        : undefined) ??
                                    (category === "BODYWEIGHT" ? "BW" : undefined)
                                }
                                className={classNames(fieldClass, "")}
                            />
                            <span className={suffixClass}>
                                {isImperial ? "lb" : "kg"}
                            </span>
                        </div>
                    </div>
                )}
                {!hasRPE && !hasPercentage && <Icon icon="custom-bolder-cross" />}

                <div className="flex-1">
                    <label htmlFor={getName("reps")} className="sr-only">
                        Reps
                    </label>
                    <div className="flex rounded-md shadow-xs">
                        <widgets.DecimalInput
                            name={getName("reps")}
                            value={handler.reps?.toString() ?? ""}
                            integerOnly
                            readOnly={readOnly}
                            onChange={(value) => {
                                if (value == null) {
                                    handler.__update({reps: value});
                                } else {
                                    handler.__update({reps: parseInt(value)});
                                }
                            }}
                            className={fieldClass}
                        />
                        <span className={suffixClass}>Reps</span>
                    </div>
                </div>
                {hasPercentage && (
                    <div className="flex-1">
                        <label
                            htmlFor={getName("weight_percentage")}
                            className="sr-only"
                        ></label>

                        <div className="flex rounded-md shadow-xs">
                            <widgets.DecimalInput
                                name={getName("weight_percentage")}
                                value={handler.weight_percentage}
                                onChange={(value) =>
                                    handler.__update({weight_percentage: value})
                                }
                                integerOnly
                                className={fieldClass}
                                readOnly={readOnly}
                            />
                            <span className={suffixClass}>%</span>
                        </div>
                    </div>
                )}
                {hasRPE && (
                    <div className="flex-1">
                        <label htmlFor={getName("rpe")} className="sr-only">
                            RPE
                        </label>
                        <div className="flex rounded-md shadow-xs">
                            <widgets.DecimalInput
                                name={getName("rpe")}
                                readOnly={readOnly}
                                onChange={(value) => handler.__update({rpe: value})}
                                value={handler.rpe}
                                className={fieldClass}
                            />
                            <span className={suffixClass}>RPE</span>
                        </div>
                    </div>
                )}
                {check}
            </>
        ))
        .exhaustive();
}

export const ExerciseSetType = (props: {
    count: number | null;
    position: number;
    type: models["core.models.ExerciseSet.Type"] | null;
}) => {
    if (props.count != null) {
        return (
            <div
                aria-hidden="true"
                className="
                    flex size-8 items-center justify-center rounded-full bg-blue-100
                    text-sm font-semibold
                "
            >
                {props.count}x
            </div>
        );
    }
    if (props.type == "WARM_UP") {
        return (
            <div
                aria-hidden="true"
                className="
                    flex size-8 items-center justify-center rounded-full bg-orange-100
                    text-xs font-semibold
                "
            >
                W
            </div>
        );
    } else if (props.type == "REST") {
        return (
            <div
                aria-hidden="true"
                className="
                    flex size-8 items-center justify-center rounded-full bg-sky-100
                    text-xs font-semibold
                "
            >
                R
            </div>
        );
    } else if (props.type == "COOL_DOWN") {
        return (
            <div
                aria-hidden="true"
                className="
                    flex size-8 items-center justify-center rounded-full bg-cyan-100
                    text-xs font-semibold
                "
            >
                C
            </div>
        );
    } else if (props.type == "MAX") {
        return (
            <div
                aria-hidden="true"
                className="
                    flex size-8 items-center justify-center rounded-full bg-orange-100
                    text-sm font-semibold
                "
            >
                <Icon icon="0222-medal-empty" />
            </div>
        );
    } else if (props.type == "FAILURE") {
        return (
            <div
                aria-hidden="true"
                className="
                    flex size-8 items-center justify-center rounded-full bg-red-100
                    text-sm font-semibold
                "
            >
                <Icon icon="0654-sad" />
            </div>
        );
    } else if (props.type == "AMRAP") {
        return (
            <div
                aria-hidden="true"
                className="
                    flex size-8 items-center justify-center rounded-full bg-yellow-100
                    text-sm font-semibold
                "
            >
                <Icon icon="0516-rank3" />
            </div>
        );
    }

    return (
        <div
            aria-hidden="true"
            className="
                flex size-8 items-center justify-center rounded-full bg-blue-100 text-sm
                font-semibold
            "
        >
            {props.position}
        </div>
    );
};

export function ExerciseSetsPreview({
    exercise,
    readOnly,
}: {
    exercise: {
        type: {
            category: server.journal.workouts.ExerciseType.output["category"];
        };
        sets: Array<
            | workouts.ExerciseSet
            | server.journal.workouts.ExerciseSet.output
            | server.journal.workouts.ExerciseSet.input
        >;
    };
    readOnly?: boolean;
}) {
    const {category} = exercise.type;

    // The fields preview only knows how to draw strength sets; cardio has its own
    // editors and isn't previewed field-by-field, so show its one-line session
    // summary instead. This also narrows `category` to StrengthCategory below.
    if (category == "CARDIO") {
        return (
            <div className="text-sm text-gray-600">{cardioSummary(exercise.sets)}</div>
        );
    }

    return (
        <>
            {exercise.sets.map((set, exerciseSetIndex) => (
                <div key={set.uuid} className="flex items-center gap-2">
                    <div>
                        <ExerciseSetType
                            count={null}
                            type={set.type}
                            position={getSetPosition(exercise.sets, exerciseSetIndex)}
                        />
                    </div>
                    <ExerciseSetFields
                        handler={
                            "__update" in set
                                ? set
                                : {...set, suggested_weight: null, __update: () => {}}
                        }
                        isTemplate={true}
                        readOnly={readOnly ?? true}
                        category={category}
                    />
                </div>
            ))}
        </>
    );
}
